import { Component, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core'

import { classes } from '../../utils/css'

@Component({
  tag: 'ui-textarea',
})
export class Textarea {
  @Prop() value: string
  @Prop() required: boolean
  @Prop() name: string
  @Prop() disabled: boolean
  @Prop() placeholder: string
  @Prop() autofocus: boolean
  @Prop() autoselect: boolean
  @Prop() validationError: string
  @Prop() renderValidationErrors = true
  @Prop() buttonPaddingRight?: string
  @Prop() bordered = true
  @Prop() outlined = true

  @Event() inputValidationError: EventEmitter<string>

  @State() pristine = true
  @State() invalid = false

  textareaEl: HTMLTextAreaElement

  get errorMessage() {
    return this.invalid && (this.validationError || (this.textareaEl && this.textareaEl.validationMessage))
  }

  @Watch('invalid')
  setAria() {
    if (this.invalid) {
      this.textareaEl.setAttribute('aria-invalid', '')
    } else {
      this.textareaEl.removeAttribute('aria-invalid')
    }
    this.inputValidationError.emit(this.errorMessage)
  }

  @Listen('input')
  onInput() {
    if (this.invalid || !this.pristine) {
      this.invalid = !this.textareaEl.validity.valid
    }

    // Autosize scroll height while having resize set to none
    this.textareaEl.style.height = '0'
    this.textareaEl.style.height = this.textareaEl.scrollHeight + 'px'
    this.textareaEl.style.overflowY = this.textareaEl.scrollHeight > 176 ? 'auto' : 'hidden'
  }

  @Watch('value')
  onValue() {
    // reset input height if value is reset by parent:
    if (!this.value) {
      this.textareaEl.style.removeProperty('height')
    }
  }

  @Method()
  async clear() {
    this.textareaEl.value = ''
    this.pristine = true
    this.invalid = false
  }

  componentDidLoad() {
    if (this.autofocus) {
      this.textareaEl.focus()
    }
    if (this.autoselect) {
      this.textareaEl.focus()
      this.textareaEl.select()
    }
  }

  render() {
    return (
      <Host
        class={`
          py-8px px-11px relative flex bg-transparent truncate group
          leading-none rounded-md items-center
          ${classes(this.outlined, 'focus-within:shadow-outline')}
          ${classes(this.bordered, 'border border-surface-highest')}
          ${classes(this.disabled, 'pointer-events-none opacity-50')}
          ${classes(this.invalid, 'border border-info-error')}
        `}
        style={{
          outline: 'none',
        }}
      >
        <textarea
          ref={(el) => (this.textareaEl = el)}
          name={this.name}
          rows={1}
          value={this.value}
          required={this.required}
          disabled={this.disabled}
          placeholder={this.placeholder}
          class={`
            bg-transparent flex-grow align-top resize-none
            text-base placeholder-surface-higher max-h-44
            ${classes(this.invalid, ' text-info-error')}
          `}
          style={{ outline: 'none' }}
        />
        <em
          hidden={!this.renderValidationErrors || !(this.invalid && !this.pristine)}
          class='block mt-2 text-2xs antialiased not-italic font-medium text-info-error'
        >
          {this.errorMessage}
        </em>
      </Host>
    )
  }
}
